import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "./AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", description: "", price_display: "", advance_display: "",
  features_str: "", is_recommended: false, sort_order: 10,
};

function RateCardPanel() {
  const [pdfUrl, setPdfUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setPdfUrl((data?.rate_card?.pdf_url) || "");
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    if (file.type !== "application/pdf") {
      toast.error("Rate card must be a PDF.");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/rate-card/upload`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfUrl(data?.rate_card_pdf_url || "");
      toast.success("Rate card updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="rate-card-panel" style={{ background: "#FFFFFF", border: "1.5px solid var(--folk-border)", borderRadius: 14, padding: 22, marginBottom: 28 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Rate card PDF (latest)</div>
      <div className="folk-serif" style={{ fontSize: 22, marginTop: 4, marginBottom: 12, textTransform: "lowercase", letterSpacing: "-0.028em" }}>the pdf every homeowner receives.</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        {pdfUrl ? (
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="rate-card-current" className="folk-pill" style={{ padding: "8px 14px", fontSize: 13, background: "transparent", color: "#68280E", border: "1.5px solid #68280E", textDecoration: "none" }}>
            View current PDF ↗
          </a>
        ) : (
          <span style={{ fontSize: 13, color: "var(--folk-muted)" }}>No rate card uploaded yet.</span>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="rate-card-upload-btn"
          disabled={uploading}
          className="folk-pill"
          style={{ padding: "8px 14px", fontSize: 13, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", opacity: uploading ? 0.55 : 1 }}
        >
          {uploading ? "Uploading…" : pdfUrl ? "Replace PDF" : "Upload PDF"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files)}
        />
      </div>
    </div>
  );
}

/** Admin CRUD for the pricing tiers used on the homeowner pricing page. */
export default function AdminPricing() {
  const [tiers, setTiers] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing/tiers`, { withCredentials: true });
      setTiers(res.data || []);
    } catch (e) {
      toast.error("Couldn't load tiers, please refresh.");
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditingId(t.id);
    setDraft({
      name: t.name || "", description: t.description || "",
      price_display: t.price_display || "", advance_display: t.advance_display || "",
      features_str: (t.features || []).join("\n"),
      is_recommended: !!t.is_recommended, sort_order: t.sort_order || 10,
    });
  };
  const resetDraft = () => { setEditingId(null); setDraft(EMPTY); };

  const save = async () => {
    if (!draft.name.trim() || !draft.price_display.trim()) {
      toast.error("Name and price are required.");
      return;
    }
    setBusy(true);
    const payload = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      price_display: draft.price_display.trim(),
      advance_display: draft.advance_display.trim(),
      features: draft.features_str.split("\n").map((s) => s.trim()).filter(Boolean),
      is_recommended: !!draft.is_recommended,
      sort_order: Number(draft.sort_order) || 10,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/pricing/tiers/${editingId}`, payload, { withCredentials: true });
        toast.success("Tier updated");
      } else {
        await axios.post(`${API}/admin/pricing/tiers`, payload, { withCredentials: true });
        toast.success("Tier added");
      }
      resetDraft();
      await load();
    } catch (e) {
      toast.error("Couldn't save the tier, please try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API}/admin/pricing/tiers/${t.id}`, { is_active: !t.is_active }, { withCredentials: true });
      await load();
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pricing/tiers/${t.id}`, { withCredentials: true });
      toast.success("Tier removed");
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <AdminShell eyebrow="Admin" title="Pricing & rate card" >
      <div data-testid="admin-pricing" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <RateCardPanel />

        {/* Existing tiers */}
        <div data-testid="tier-list" style={{ display: "grid", gap: 12 }}>
          {tiers.map((t) => (
            <div key={t.id} data-testid={`admin-tier-${t.id}`} style={{ background: "#FFFFFF", border: "1.5px solid var(--folk-border)", borderRadius: 14, padding: 18, display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div className="folk-serif" style={{ fontSize: 22 }}>{t.name}</div>
                  {t.is_recommended && <span style={{ background: "#FFFBC9", color: "#68280E", fontSize: 10.5, letterSpacing: "0.14em", padding: "3px 10px", borderRadius: 999, textTransform: "uppercase", fontWeight: 600 }}>Recommended</span>}
                  {!t.is_active && <span style={{ background: "#EEE", color: "#666", fontSize: 10.5, padding: "3px 10px", borderRadius: 999 }}>Hidden</span>}
                </div>
                <div style={{ color: "var(--folk-muted)", fontSize: 13.5, lineHeight: 1.55, marginTop: 4 }}>{t.description}</div>
                <div style={{ marginTop: 6, fontSize: 13.5 }}>
                  <b>{t.price_display}</b> · advance <b>{t.advance_display}</b>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "var(--folk-muted)" }}>Features: {(t.features || []).join(" · ") || "—"}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid={`edit-tier-${t.id}`} onClick={() => startEdit(t)} className="folk-btn">Edit</button>
                <button data-testid={`toggle-tier-${t.id}`} onClick={() => toggleActive(t)} className="folk-btn">{t.is_active ? "Hide" : "Show"}</button>
                <button data-testid={`delete-tier-${t.id}`} onClick={() => remove(t)} className="folk-btn">Delete</button>
              </div>
            </div>
          ))}
          {tiers.length === 0 && <div style={{ color: "var(--folk-muted)" }}>No tiers yet. Add one below.</div>}
        </div>

        {/* Editor */}
        <div style={{ marginTop: 40, background: "#FFFFFF", border: "1.5px solid var(--folk-border)", borderRadius: 14, padding: 24 }}>
          <h2 className="folk-h2">{editingId ? "edit tier" : "add new tier"}</h2>
          <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
            <input data-testid="tier-name" className="folk-input" placeholder="Name (e.g. Design + sourcing)" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <input data-testid="tier-description" className="folk-input" placeholder="One-line description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <input data-testid="tier-price" className="folk-input" placeholder="Price display (e.g. From ₹5,50,000)" value={draft.price_display} onChange={(e) => setDraft({ ...draft, price_display: e.target.value })} />
              <input data-testid="tier-advance" className="folk-input" placeholder="Advance display (e.g. ₹1,50,000 advance)" value={draft.advance_display} onChange={(e) => setDraft({ ...draft, advance_display: e.target.value })} />
            </div>
            <textarea data-testid="tier-features" rows={4} className="folk-textarea" placeholder="Features (one per line)" value={draft.features_str} onChange={(e) => setDraft({ ...draft, features_str: e.target.value })} />
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <input data-testid="tier-recommended" type="checkbox" checked={draft.is_recommended} onChange={(e) => setDraft({ ...draft, is_recommended: e.target.checked })} />
                Recommended (only one should be recommended)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                Sort order
                <input data-testid="tier-order" type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} style={{ width: 80 }} className="folk-input" />
              </label>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button data-testid="save-tier" onClick={save} disabled={busy} className="folk-pill" style={{ opacity: busy ? 0.6 : 1 }}>
                {editingId ? "Save changes" : "Add tier"}
              </button>
              {editingId && <button onClick={resetDraft} className="folk-btn">Cancel</button>}
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
